import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Alert,
  Autocomplete,
  TextField
} from '@mui/material';
import SyncWizardFooterWrapper from 'components/SyncWizardFooterWrapper';
import useAsync from 'hooks/useAsync';

export const CrosstabsStep2 = ({
  projectId,
  selectedDataset,
  surveyResponseTable,
  questionLabelsTable,
  responseLabelsTable,
  questionIdField,
  setQuestionIdField,
  questionTextField,
  setQuestionTextField,
  questionTypeField,
  setQuestionTypeField,
  parentQuestionIdField,
  setParentQuestionIdField,
  optionTextField,
  setOptionTextField,
  responseField,
  setResponseField,
  weightKeyField,
  setWeightKeyField,
  showErrors,
  onNext,
  onBack
}) => {
  // Available columns from different tables
  const [surveyResponseColumns, setSurveyResponseColumns] = useState([]);
  const [questionLabelsColumns, setQuestionLabelsColumns] = useState([]);
  const [responseLabelsColumns, setResponseLabelsColumns] = useState([]);
  const [weightKeyOptions, setWeightKeyOptions] = useState([]);

  // Async functions to fetch table schemas
  const fetchSurveyResponseSchema = useAsync(async () => {
    if (!surveyResponseTable) return [];
    
    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", selectedDataset);
    queryParams.set("table_id", surveyResponseTable);
    
    const response = await fetch(`/bigquery/get-table-schema/?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const schema = await response.json();
    const columnNames = schema
      .map(({ name }) => name)
      .filter((name) => !name.startsWith("_"));
    
    setSurveyResponseColumns(columnNames);
    return columnNames;
  });

  const fetchQuestionLabelsSchema = useAsync(async () => {
    if (!questionLabelsTable) return [];
    
    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", selectedDataset);
    queryParams.set("table_id", questionLabelsTable);
    
    const response = await fetch(`/bigquery/get-table-schema/?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const schema = await response.json();
    const columnNames = schema
      .map(({ name }) => name)
      .filter((name) => !name.startsWith("_"));
    
    setQuestionLabelsColumns(columnNames);
    return columnNames;
  });

  const fetchResponseLabelsSchema = useAsync(async () => {
    if (!responseLabelsTable) return [];
    
    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", selectedDataset);
    queryParams.set("table_id", responseLabelsTable);
    
    const response = await fetch(`/bigquery/get-table-schema/?${queryParams.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const schema = await response.json();
    const columnNames = schema
      .map(({ name }) => name)
      .filter((name) => !name.startsWith("_"));
    
    setResponseLabelsColumns(columnNames);
    return columnNames;
  });

  const fetchWeightKeys = useAsync(async () => {
    if (!questionLabelsTable || !questionIdField || !questionTextField || !questionTypeField) return [];
    
    // Query CNL table for weight keys using user-specified column names
    const queryParams = new URLSearchParams();
    queryParams.set("project_id", projectId);
    queryParams.set("dataset_id", selectedDataset);
    queryParams.set("table_id", questionLabelsTable);
    queryParams.set("question_id_field", questionIdField);
    queryParams.set("question_text_field", questionTextField);
    queryParams.set("question_type_field", questionTypeField);
    
    try {
      const response = await fetch(`/crosstabs/get-weight-keys/?${queryParams.toString()}`);
      if (!response.ok) {
        console.warn("Weight keys endpoint not available, using survey response columns as fallback");
        setWeightKeyOptions(surveyResponseColumns.map(col => ({ value: col, label: col })));
        return surveyResponseColumns;
      }
      
      const weightData = await response.json();
      const weightOptions = weightData.map(row => ({
        value: row.q_id,
        label: `${row.q_id} - ${row.q_text}`
      }));
      
      setWeightKeyOptions(weightOptions);
      return weightOptions;
    } catch (error) {
      console.warn("Error fetching weight keys, using survey response columns as fallback:", error);
      setWeightKeyOptions(surveyResponseColumns.map(col => ({ value: col, label: col })));
      return surveyResponseColumns;
    }
  });

  // Load schemas when tables are available
  useEffect(() => {
    if (surveyResponseTable && selectedDataset) {
      fetchSurveyResponseSchema.perform();
    }
  }, [surveyResponseTable, selectedDataset]);

  useEffect(() => {
    if (questionLabelsTable && selectedDataset) {
      fetchQuestionLabelsSchema.perform();
    }
  }, [questionLabelsTable, selectedDataset]);

  useEffect(() => {
    if (responseLabelsTable && selectedDataset) {
      fetchResponseLabelsSchema.perform();
    }
  }, [responseLabelsTable, selectedDataset]);

  useEffect(() => {
    if (questionLabelsTable && selectedDataset && surveyResponseColumns.length > 0 && 
        questionIdField && questionTextField && questionTypeField) {
      fetchWeightKeys.perform();
    }
  }, [questionLabelsTable, selectedDataset, surveyResponseColumns, questionIdField, questionTextField, questionTypeField]);

  // Show loading state while fetching essential schemas (not weight keys since they're optional)
  const isLoading = fetchSurveyResponseSchema.isRunning || 
                   fetchQuestionLabelsSchema.isRunning || 
                   fetchResponseLabelsSchema.isRunning;

  const hasErrors = fetchSurveyResponseSchema.didFail || 
                   fetchQuestionLabelsSchema.didFail || 
                   fetchResponseLabelsSchema.didFail ||
                   fetchWeightKeys.didFail;

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          Map Question & Response Fields
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary', fontStyle: 'italic' }}>
          Choose columns from pol_data that correspond to each field
        </Typography>

        {isLoading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Loading table schemas...
          </Alert>
        )}

        {hasErrors && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Error loading table schemas. Please check your table selections and try again.
          </Alert>
        )}

        {!isLoading && !hasErrors && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Question ID Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Question ID
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  options={questionLabelsColumns}
                  value={questionIdField || null}
                  onChange={(event, newValue) => setQuestionIdField(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Column from question labels table"
                      error={showErrors && !questionIdField}
                      helperText={showErrors && !questionIdField ? 'Please select a question ID field' : ''}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Question Text Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Question Text
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  options={questionLabelsColumns}
                  value={questionTextField || null}
                  onChange={(event, newValue) => setQuestionTextField(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Column from question labels table"
                      error={showErrors && !questionTextField}
                      helperText={showErrors && !questionTextField ? 'Please select a question text field' : ''}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Question Type Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Question Type
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  options={questionLabelsColumns}
                  value={questionTypeField || null}
                  onChange={(event, newValue) => setQuestionTypeField(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Column from question labels table"
                      error={showErrors && !questionTypeField}
                      helperText={showErrors && !questionTypeField ? 'Please select a question type field' : ''}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Parent Question ID Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Parent Question ID
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  options={questionLabelsColumns}
                  value={parentQuestionIdField || null}
                  onChange={(event, newValue) => setParentQuestionIdField(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Column from question labels table (Optional)"
                      helperText="Field that identifies parent questions for filtering child questions"
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Option Text Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Option Text
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  options={responseLabelsColumns}
                  value={optionTextField || null}
                  onChange={(event, newValue) => setOptionTextField(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Column from response labels table"
                      error={showErrors && !optionTextField}
                      helperText={showErrors && !optionTextField ? 'Please select an option text field' : ''}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Response Field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Response
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  options={responseLabelsColumns}
                  value={responseField || null}
                  onChange={(event, newValue) => setResponseField(newValue || '')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Column from response labels table"
                      error={showErrors && !responseField}
                      helperText={showErrors && !responseField ? 'Please select a response field' : ''}
                    />
                  )}
                />
              </Box>
            </Box>

            {/* Weight Key Field (Optional) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body1" sx={{ minWidth: 120, fontWeight: 500 }}>
                Weight Key
              </Typography>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ color: 'text.secondary' }}>→</Typography>
                <Autocomplete
                  fullWidth
                  disabled={!questionIdField || !questionTextField || !questionTypeField}
                  options={weightKeyOptions}
                  value={weightKeyOptions.find(option => option.value === weightKeyField) || null}
                  onChange={(event, newValue) => setWeightKeyField(newValue ? newValue.value : '')}
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Weight column (Optional)"
                    />
                  )}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Navigation Buttons */}
        <SyncWizardFooterWrapper sxOverrides={{ justifyContent: "space-between", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            size="large"
            sx={{ px: 4, py: 1.5 }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={onNext}
            disabled={isLoading || hasErrors}
            size="large"
            sx={{ 
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              backgroundColor: 'grey.600',
              '&:hover': {
                backgroundColor: 'grey.700'
              }
            }}
          >
            Next: Select Row Questions
          </Button>
        </SyncWizardFooterWrapper>
      </Box>
    </Box>
  );
};

export default CrosstabsStep2;